import { useState } from 'react';

import { Button } from '../../button';
import { Input } from '../../input';

import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';
import {
  controlledSubmit,
  currentHash,
  currentLocation,
  effectTransition,
  initialEffect,
  initialLift,
  initialRender,
  initialRouter,
  initialStale,
  keyedRows,
  liftTransition,
  parseRoute,
  projectNames,
  renderTransition,
  routeComponent,
  routerTransition,
  staleMismatch,
  staleTransition,
  type BusyOwner,
  type EffectAction,
  type KeyRule,
  type LiftAction,
  type RenderAction,
  type StaleAction,
  type SubmitOutcome,
  type UiTask,
} from './react-models';

import './relationship-labs.css';

function Predict({ children }: { children: string }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Table({
  label,
  columns,
  rows,
}: {
  label: string;
  columns: string[];
  rows: (string | number | null)[][];
}) {
  const template = `repeat(${columns.length}, minmax(0, 1fr))`;
  return (
    <div className="rel-table-scroll">
      <div className="rel-table cb-record-grid" role="table" aria-label={label}>
        <div
          role="row"
          className="rel-row rel-table-head"
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((column) => (
            <span key={column} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {rows.length ? (
          rows.map((row, index) => (
            <div
              role="row"
              className="rel-row"
              key={index}
              style={{ gridTemplateColumns: template }}
            >
              {row.map((cell, column) => (
                <span role="cell" key={column} data-null={cell === null || undefined}>
                  {cell ?? 'NULL'}
                </span>
              ))}
            </div>
          ))
        ) : (
          <div role="row" className="rel-row">
            <span role="cell">No rows</span>
          </div>
        )}
      </div>
    </div>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
const taskRows = (rows: UiTask[]) => rows.map((row) => [row.id, row.title, String(row.completed)]);
const SIMULATION = 'A simulation of React’s behavior.';

export function ReactRenderLab() {
  const [state, setState] = useState(initialRender);
  const act = (action: RenderAction) => setState((current) => renderTransition(current, action));
  const agree = JSON.stringify(state.state) === JSON.stringify(state.rendered);
  return (
    <Lab
      title="What makes React redraw the list?"
      eyebrow="React model · state and rendering"
      className="rel-lab"
    >
      <Predict>
        Change tasks[0].completed directly, then change it through setTasks. Which change reaches
        the screen?
      </Predict>
      <Facts
        values={[
          ['Renders so far', String(state.renders)],
          ['State and screen agree', agree ? 'yes' : 'no'],
        ]}
      />
      <div className="cb-two">
        <Panel label="State array (what the code holds)">
          <Table
            label="State array"
            columns={['id', 'title', 'completed']}
            rows={taskRows(state.state)}
          />
        </Panel>
        <Panel label="Rendered rows (what the screen shows)">
          <Table
            label="Rendered rows"
            columns={['id', 'title', 'completed']}
            rows={taskRows(state.rendered)}
          />
        </Panel>
      </div>
      <div className="cb-controls">
        <Button size="sm" onClick={() => act('set-complete')}>
          Complete task 1 with setTasks
        </Button>
        <Button size="sm" variant="secondary" onClick={() => act('mutate')}>
          Mutate tasks[0].completed directly
        </Button>
        <Button size="sm" variant="secondary" onClick={() => act('add')}>
          Add a task with setTasks
        </Button>
        <Reset onClick={() => act('reset')} />
      </div>
      <Result tone={agree ? 'neutral' : 'warning'}>{state.message}</Result>
      <p className="rel-caption">
        {SIMULATION} React renders again only when a setter receives a new value; it never watches
        an array for changes made in place.
      </p>
    </Lab>
  );
}

export function ReactKeysLab() {
  const [rule, setRule] = useState<KeyRule>('index');
  const [draftTask, setDraftTask] = useState('8');
  const [draft, setDraft] = useState('');
  const [deleted, setDeleted] = useState<number | null>(null);
  const result = keyedRows(rule, Number(draftTask), draft, deleted);
  const reset = () => {
    setRule('index');
    setDraftTask('8');
    setDraft('');
    setDeleted(null);
  };
  return (
    <Lab
      title="Which row keeps its typed text when a row is removed?"
      eyebrow="React model · list keys"
      className="rel-lab"
    >
      <Predict>
        Each row has its own draft box. Type into one row, delete another task, and predict where
        the text ends up under each key rule.
      </Predict>
      <div className="cb-controls">
        <label>
          Key rule
          <ModelSelect
            aria-label="Key rule"
            value={rule}
            onValueChange={(value) => setRule(value as KeyRule)}
            options={[
              { value: 'index', label: 'key={index} · the row position' },
              { value: 'id', label: 'key={task.id} · the record identity' },
            ]}
          />
        </label>
        <label>
          Type the draft into
          <ModelSelect
            aria-label="Draft row"
            value={draftTask}
            disabled={deleted !== null}
            onValueChange={(value) => setDraftTask(value)}
            options={[
              { value: '8', label: 'the row for task 8' },
              { value: '4', label: 'the row for task 4' },
            ]}
          />
        </label>
        <label>
          Draft text
          <Input
            aria-label="Draft text"
            value={draft}
            maxLength={60}
            disabled={deleted !== null}
            onChange={(event) => setDraft(event.target.value)}
          />
        </label>
      </div>
      <Table
        label="Rows after reconciliation"
        columns={['id', 'title', 'draft box shows']}
        rows={result.rows.map((row) => [row.id, row.title, row.draft || '(empty)'])}
      />
      <div className="cb-controls">
        <Button size="sm" disabled={deleted !== null} onClick={() => setDeleted(4)}>
          Delete task 4
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={deleted !== null}
          onClick={() => setDeleted(1)}
        >
          Delete task 1
        </Button>
        <Reset onClick={reset} />
      </div>
      <Result
        tone={
          result.outcome === 'lost' || result.outcome === 'moved'
            ? 'warning'
            : result.outcome === 'none'
              ? 'neutral'
              : 'success'
        }
      >
        {result.message}
      </Result>
      <p className="rel-caption">
        {SIMULATION} A key tells React which earlier instance a row continues. Position changes when
        the list changes; a task’s id does not.
      </p>
    </Lab>
  );
}

export function ReactControlledLab() {
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<SubmitOutcome | null>(null);
  const submit = () => {
    const result = controlledSubmit(draft, busy);
    setOutcome(result);
    if (result.kind === 'sent') setDraft('');
  };
  return (
    <Lab
      title="Who owns the value in the box?"
      eyebrow="React model · controlled input"
      className="rel-lab"
    >
      <Predict>
        Type a title, mark the page busy, then try to submit. What does the box show while the page
        waits?
      </Predict>
      <div className="cb-controls">
        <label>
          Task title
          <Input
            aria-label="Task title"
            value={draft}
            maxLength={120}
            disabled={busy}
            onChange={(event) => {
              setDraft(event.target.value);
              setOutcome(null);
            }}
          />
        </label>
        <Button size="sm" disabled={busy} onClick={submit}>
          Submit
        </Button>
        <Button
          size="sm"
          variant="secondary"
          aria-pressed={busy}
          onClick={() => {
            setBusy(!busy);
            setOutcome(null);
          }}
        >
          Page is busy
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={busy}
          onClick={() => {
            setDraft('   ');
            setOutcome(null);
          }}
        >
          Try a blank title
        </Button>
        <Reset
          onClick={() => {
            setDraft('');
            setBusy(false);
            setOutcome(null);
          }}
        />
      </div>
      <div className="cb-two">
        <Panel label="State">
          <Facts
            values={[
              ['draft', JSON.stringify(draft)],
              ['busy', String(busy)],
            ]}
          />
        </Panel>
        <Panel label="Last request sent">
          <pre aria-label="Request sent">
            {outcome?.kind === 'sent' ? outcome.request : '(no request sent)'}
          </pre>
        </Panel>
      </div>
      <Result tone={outcome ? (outcome.kind === 'sent' ? 'success' : 'warning') : 'neutral'}>
        {outcome
          ? outcome.message
          : 'The input’s value attribute is the draft state. Every keystroke calls onChange, which calls setDraft, which renders the new value into the box.'}
      </Result>
      <p className="rel-caption">
        {SIMULATION} Reading the value from state means the submit handler, the disabled rule and
        the preserved draft all use one source.
      </p>
    </Lab>
  );
}

export function ReactEffectLab() {
  const [state, setState] = useState(initialEffect);
  const act = (action: EffectAction) => setState((current) => effectTransition(current, action));
  const load = state.load;
  return (
    <Lab
      title="When does a load run, and what does the page show meanwhile?"
      eyebrow="React model · effects"
      className="rel-lab"
    >
      <Predict>
        Mount the page and change its filter before the first answer arrives. How many requests were
        sent, and which answer should the page keep?
      </Predict>
      <Facts
        values={[
          ['Mounted', state.mounted ? 'yes' : 'no'],
          ['Filter dependency', state.filter],
          ['Load state', load.kind],
          ['Request waiting', state.pending ? 'yes' : 'no'],
        ]}
      />
      <div className="cb-two">
        <Panel label="Screen">
          {load.kind === 'ready' && load.rows.length ? (
            <Table
              label="Effect rows"
              columns={['id', 'title', 'completed']}
              rows={taskRows(load.rows)}
            />
          ) : (
            <pre aria-label="Screen">
              {load.kind === 'idle'
                ? 'Nothing is rendered; the component is not on the page.'
                : load.kind === 'loading'
                  ? 'Loading tasks…'
                  : load.kind === 'failed'
                    ? `Could not load tasks. ${load.message}`
                    : 'No tasks in this view.'}
            </pre>
          )}
        </Panel>
        <Panel label="Effect log">
          <pre aria-label="Effect log">
            {state.log.length ? state.log.join('\n') : '(nothing has run yet)'}
          </pre>
        </Panel>
      </div>
      <div className="cb-controls">
        <Button size="sm" disabled={state.mounted} onClick={() => act('mount')}>
          Mount the task page
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={!state.mounted}
          onClick={() => act('change-filter')}
        >
          Change filter
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={!state.mounted}
          onClick={() => act('unmount')}
        >
          Unmount
        </Button>
      </div>
      <div className="cb-controls cb-secondary">
        <Button size="sm" variant="secondary" onClick={() => act('respond-rows')}>
          Respond with rows
        </Button>
        <Button size="sm" variant="secondary" onClick={() => act('respond-empty')}>
          Respond empty
        </Button>
        <Button size="sm" variant="secondary" onClick={() => act('respond-error')}>
          Respond with error
        </Button>
        <Reset onClick={() => act('reset')} />
      </div>
      <Result tone={load.kind === 'failed' ? 'warning' : 'neutral'}>{state.message}</Result>
      <p className="rel-caption">
        {SIMULATION} The effect runs after the first render and again whenever a dependency changes;
        its cleanup runs before the next run and on unmount.
      </p>
    </Lab>
  );
}

export function ReactStaleLab() {
  const [state, setState] = useState(() => initialStale());
  const act = (action: StaleAction) => setState((current) => staleTransition(current, action));
  const mismatch = staleMismatch(state);
  const heading =
    state.route === null ? 'No project open' : `Project: ${projectNames[state.route]}`;
  return (
    <Lab
      title="Which project’s tasks appear if you switch quickly?"
      eyebrow="React model · stale responses"
      className="rel-lab"
    >
      <Predict>
        Open Apollo, open Support before Apollo answers, deliver Support, then deliver Apollo. Which
        rows does the Support page show?
      </Predict>
      <div className="cb-controls">
        <Button
          size="sm"
          variant="secondary"
          aria-pressed={state.guard}
          onClick={() => act('toggle-guard')}
        >
          Cleanup guard
        </Button>
        <Button size="sm" onClick={() => act('open-1')}>
          Open Apollo (1)
        </Button>
        <Button size="sm" onClick={() => act('open-2')}>
          Open Support (2)
        </Button>
        <Button size="sm" variant="secondary" onClick={() => act('deliver-1')}>
          Deliver Apollo answer
        </Button>
        <Button size="sm" variant="secondary" onClick={() => act('deliver-2')}>
          Deliver Support answer
        </Button>
        <Reset onClick={() => act('reset')} />
      </div>
      <Facts
        values={[
          ['Current route', state.route === null ? '(none)' : `#/projects/${state.route}`],
          [
            'Requests waiting',
            state.pending.length
              ? state.pending.map((id) => `GET /projects/${id}/tasks`).join(', ')
              : 'none',
          ],
        ]}
      />
      <div className="cb-two">
        <Panel label="Screen">
          <pre aria-label="Screen heading">{heading}</pre>
          {state.route === null ? null : state.rows === null ? (
            <p className="rel-caption">Loading tasks…</p>
          ) : (
            <Table
              label="Project page rows"
              columns={['id', 'title', 'assignee']}
              rows={state.rows.map((row) => [row.id, row.title, row.assignee])}
            />
          )}
        </Panel>
        <Panel label="Request log">
          <pre aria-label="Stale log">
            {state.log.length ? state.log.join('\n') : '(nothing has happened yet)'}
          </pre>
        </Panel>
      </div>
      <Result tone={mismatch ? 'warning' : state.rows ? 'success' : 'neutral'}>
        {state.message}
      </Result>
      <p className="rel-caption">
        {SIMULATION} The guard is a flag the effect’s cleanup sets; the response handler checks it
        before calling a setter.
      </p>
    </Lab>
  );
}

export function ReactRouterLab() {
  const [state, setState] = useState(() => initialRouter());
  const route = parseRoute(currentHash(state));
  const hash = state.mode === 'hash';
  const link = (target: '/' | '/projects' | '/projects/1') => (hash ? `#${target}` : target);
  return (
    <Lab
      title="What changes the page without asking the server?"
      eyebrow="React model · navigation"
      className="rel-lab"
    >
      <Predict>
        Follow a link, press Back, then reload. Which of those three actions sends a request to the
        server?
      </Predict>
      <div className="cb-controls">
        <Button
          size="sm"
          variant="secondary"
          aria-pressed={!hash}
          onClick={() => setState((current) => routerTransition(current, { kind: 'toggle-mode' }))}
        >
          Path routes
        </Button>
        <Button
          size="sm"
          onClick={() =>
            setState((current) => routerTransition(current, { kind: 'go', target: '/' }))
          }
        >
          Link to {link('/')}
        </Button>
        <Button
          size="sm"
          onClick={() =>
            setState((current) => routerTransition(current, { kind: 'go', target: '/projects' }))
          }
        >
          Link to {link('/projects')}
        </Button>
        <Button
          size="sm"
          onClick={() =>
            setState((current) => routerTransition(current, { kind: 'go', target: '/projects/1' }))
          }
        >
          Link to {link('/projects/1')}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => setState((current) => routerTransition(current, { kind: 'back' }))}
        >
          Back
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => setState((current) => routerTransition(current, { kind: 'reload' }))}
        >
          Reload
        </Button>
        <Reset
          onClick={() => setState((current) => routerTransition(current, { kind: 'reset' }))}
        />
      </div>
      <Facts
        values={[
          ['Location', currentLocation(state)],
          ['Parsed route', JSON.stringify(route)],
          ['Component rendered', routeComponent(route)],
        ]}
      />
      <Panel label="Requests the server received">
        <pre aria-label="Server request log">{state.requests.join('\n')}</pre>
      </Panel>
      <Result
        tone={
          state.mode === 'path' &&
          route.page === 'project' &&
          state.requests.at(-1)?.includes('/projects/1')
            ? 'warning'
            : 'neutral'
        }
      >
        {state.message}
      </Result>
      <p className="rel-caption">
        {SIMULATION} Our API already answers paths such as /projects/1, so the client keeps its page
        state in the hash, which the browser never sends.
      </p>
    </Lab>
  );
}

export function ReactLiftLab() {
  const [state, setState] = useState(() => initialLift());
  const act = (action: LiftAction) => setState((current) => liftTransition(current, action));
  const disabledSummary =
    state.phase !== 'creating'
      ? 'none'
      : state.owner === 'page'
        ? 'the input, Add and every Delete button'
        : 'the input and Add only';
  const inFlight = state.phase === 'creating' ? (state.overlapped ? 2 : 1) : 0;
  const tone = state.overlapped ? 'warning' : state.phase === 'done' ? 'success' : 'neutral';
  return (
    <Lab
      title="Who must know that the page is busy?"
      eyebrow="React model · lifted state"
      className="rel-lab"
    >
      <Predict>
        Start a create, then press Delete on task 4 before it finishes. With which owner does the
        delete go through, and what does the list show when the create completes?
      </Predict>
      <div className="cb-controls">
        <label>
          Busy flag owner
          <ModelSelect
            aria-label="Busy flag owner"
            value={state.owner}
            onValueChange={(value) => setState(initialLift(value as BusyOwner))}
            options={[
              { value: 'form', label: 'The form owns busy' },
              { value: 'page', label: 'The page owns busy' },
            ]}
          />
        </label>
        <Button size="sm" onClick={() => act({ kind: 'start-create' })}>
          Start a create
        </Button>
        <Button size="sm" variant="secondary" onClick={() => act({ kind: 'press-delete', id: 4 })}>
          Press Delete on task 4
        </Button>
        <Button size="sm" variant="secondary" onClick={() => act({ kind: 'finish-create' })}>
          Finish the create
        </Button>
        <Reset onClick={() => act({ kind: 'reset' })} />
      </div>
      <div className="cb-two">
        <Panel label="Screen">
          <div className="cb-controls">
            <label>
              Task title
              <Input
                aria-label="Screen: Task title"
                value={state.draft}
                readOnly
                disabled={state.disabled.input}
              />
            </label>
            <Button
              size="sm"
              aria-label="Screen: Add task"
              disabled={state.disabled.add}
              onClick={() => act({ kind: 'start-create' })}
            >
              Add task
            </Button>
          </div>
          <ul className="cb-memory" aria-label="Screen list">
            {state.rows.map((task) => (
              <li key={task.id}>
                <span>
                  #{task.id} {task.title}
                </span>{' '}
                <Button
                  size="sm"
                  variant="secondary"
                  aria-label={`Screen: Delete task ${task.id}`}
                  disabled={state.disabled.deletes[task.id] ?? false}
                  onClick={() => act({ kind: 'press-delete', id: task.id })}
                >
                  Delete
                </Button>
              </li>
            ))}
          </ul>
        </Panel>
        <Panel label="Who can see the flag">
          <Facts
            values={[
              ['busy lives in', state.owner === 'form' ? 'CreateTaskForm' : 'TaskPage'],
              ['phase', state.phase],
              ['disabled now', disabledSummary],
              ['writes in flight', String(inFlight)],
            ]}
          />
        </Panel>
      </div>
      <Result tone={tone}>{state.message}</Result>
      <p className="rel-caption">
        {SIMULATION} A prop is the only way a row learns that the page is busy; a flag kept inside
        the form never reaches the rows.
      </p>
    </Lab>
  );
}
